import { useSyncExternalStore } from 'react';
import { ApiError, rtApi } from '../api';
import type { RtInfo, RtSample } from '../types';

/**
 * A well's real-time feed, shared: the status strip and every RT screen read
 * one connection, not one each. It opens when the first reader arrives, loads
 * the last hours, then follows the stream; a dropped stream is reopened from
 * the last sample received, so nothing is missed. It closes a little after
 * the last reader leaves, so moving between tabs does not reconnect.
 */

export type FeedStatus = 'connecting' | 'live' | 'reconnecting' | 'none' | 'error';

export interface FeedSnapshot {
  status: FeedStatus;
  info: RtInfo | null;
  /** Oldest first, the last HISTORY_HOURS of them. */
  samples: RtSample[];
  latest: RtSample | null;
  error: string | null;
}

const HISTORY_HOURS = 6;
const LINGER_MS = 10_000;

class Feed {
  private listeners = new Set<() => void>();
  private abort: AbortController | null = null;
  private linger: number | undefined;
  snapshot: FeedSnapshot = { status: 'connecting', info: null, samples: [], latest: null, error: null };

  private readonly wellId: number;

  constructor(wellId: number) {
    this.wellId = wellId;
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    window.clearTimeout(this.linger);
    if (!this.abort) void this.start();
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) {
        this.linger = window.setTimeout(() => this.stop(), LINGER_MS);
      }
    };
  };

  private set(patch: Partial<FeedSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const l of this.listeners) l();
  }

  private stop() {
    this.abort?.abort();
    this.abort = null;
    feeds.delete(this.wellId);
  }

  private async start() {
    const abort = new AbortController();
    this.abort = abort;
    try {
      const info = await rtApi.info(this.wellId);
      const samples = await rtApi.history(this.wellId, HISTORY_HOURS * 60, 2500);
      if (abort.signal.aborted) return;
      this.set({ info, samples, latest: samples.at(-1) ?? info.latest ?? null });
    } catch (err) {
      if (abort.signal.aborted) return;
      const none = err instanceof ApiError && err.status === 404;
      this.set({
        status: none ? 'none' : 'error',
        error: none ? null : err instanceof ApiError ? err.message : 'Could not reach the real-time feed.',
      });
      return;
    }
    await this.follow(abort.signal);
  }

  /** Reads the stream, and reopens it after a drop, waiting longer each time. */
  private async follow(signal: AbortSignal) {
    let wait = 2000;
    while (!signal.aborted) {
      try {
        const since = this.snapshot.latest ? Date.parse(this.snapshot.latest.t) : null;
        this.set({ status: 'live', error: null });
        await rtApi.stream(this.wellId, since, (s) => {
          wait = 2000;
          this.add(s);
        }, signal);
      } catch (err) {
        if (signal.aborted) return;
        if (err instanceof ApiError && err.status === 404) {
          this.set({ status: 'none' });
          return;
        }
      }
      if (signal.aborted) return;
      this.set({ status: 'reconnecting' });
      await new Promise((resolve) => window.setTimeout(resolve, wait));
      wait = Math.min(wait * 2, 30_000);
    }
  }

  private add(sample: RtSample) {
    const last = this.snapshot.latest;
    if (last && Date.parse(sample.t) <= Date.parse(last.t)) return;
    const cut = Date.parse(sample.t) - HISTORY_HOURS * 3_600_000;
    let samples = this.snapshot.samples;
    let drop = 0;
    while (drop < samples.length && Date.parse(samples[drop].t) < cut) drop++;
    samples = [...samples.slice(drop), sample];
    this.set({ samples, latest: sample });
  }
}

const feeds = new Map<number, Feed>();

function feedFor(wellId: number) {
  let feed = feeds.get(wellId);
  if (!feed) {
    feed = new Feed(wellId);
    feeds.set(wellId, feed);
  }
  return feed;
}

const IDLE: FeedSnapshot = { status: 'none', info: null, samples: [], latest: null, error: null };
const noop = () => () => undefined;

/** The feed of a well, or an idle one for null (a well not being watched). */
export function useRtFeed(wellId: number | null): FeedSnapshot {
  const feed = wellId ? feedFor(wellId) : null;
  return useSyncExternalStore(
    feed ? feed.subscribe : noop,
    () => (feed ? feed.snapshot : IDLE),
  );
}
